"use client";

import React, {
  useRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  FunctionComponent,
  PropsWithChildren,
} from "react";

import Cookies from "js-cookie";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useDebounce, useDebouncedCallback } from "use-debounce";

import { GlobalPathParams } from "~/app/[locale]/types";
import { SearchTabs } from "~/components/search-results-page/types";
import {
  BloomreachCatalogsAzureKeys,
  BR_DEFAULT_UID,
  BR_USERID_KEY,
} from "~/constants/bloomreach";
import { FUNCTIONAL_COOKIES } from "~/constants/cookie-categories";
import { DEBOUNCE_DELAY_300 } from "~/constants/delay-constants";
import { Params, Routes } from "~/constants/request";
import { LAST_SEARCHES } from "~/constants/user-cookies";
import { waitForRouterHistory } from "~/context/router-history/helper";
import { getBloomreachCatalogsAzureKeys } from "~/helpers/get-bloomreach-catalogs-azure-keys";
import { logBloomreachEvent } from "~/helpers/log-bloomreach-event";
import {
  BloomreachEventType,
  BloomreachGroupType,
} from "~/helpers/log-bloomreach-event/types";
import { useCookies } from "~/hooks/use-cookies";
import { useAzureConfigurator } from "~/services/azure-configurator/use-azure-configurator";
import { Nullable } from "~/types/general.types";
import { getRefUrlFromCookie } from "~/utils/data/bloomreach/get-ref-url-from-cookie/get-ref-url-from-cookie";
import { Logger } from "~/utils/logger";

import { HeaderContext } from "./context";
import { HeaderHelpers } from "./types";
export type HeaderProviderProps = PropsWithChildren;

export const HeaderProvider: FunctionComponent<HeaderProviderProps> = ({
  children,
}) => {
  const { locale: pageLocale } = useParams<GlobalPathParams>();
  const config = useAzureConfigurator(pageLocale);
  const router = useRouter();

  const searchParams = useSearchParams();
  const pathName = usePathname();

  const [query, setQuery] = useState<string>(
    () => (searchParams.get(Params.QUERY) as string) ?? "",
  );

  const queryParam = useMemo(
    () => (searchParams.get(Params.QUERY) as string) ?? "",
    [searchParams],
  );
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [queryCached] = useDebounce(query, DEBOUNCE_DELAY_300);

  const [lastSearches, setLastSearches] = useCookies<string[]>(LAST_SEARCHES, [], {
    oneTrustGroup: FUNCTIONAL_COOKIES,
  });

  const [suggestions, setSuggestions] = useState<(Nullable<string> | undefined)[]>();

  const brUid2 = Cookies.get(BR_USERID_KEY) || BR_DEFAULT_UID;

  const loadSuggestions = useCallback(
    async (params: { q: string; brUid2: string; brRefUrl: string }) => {
      try {
        const suggestionConfig = config?.appConfig?.bloomreachSuggestion;

        if (!suggestionConfig) {
          Logger.getLogger().warn("Bloomreach suggestion config missing.");
          setSuggestions([]);
          return;
        }

        const {
          accountId = "",
          domainKey = "",
          catalogViews = "",
          domainName = "",
          baseUrl = "",
        } = suggestionConfig;

        const paramsObj: Record<string, string> = {
          account_id: accountId,
          domain_key: domainKey,
          catalog_views: catalogViews,
          url: domainName,
          _br_uid_2: params?.brUid2 || "dummy",
          ref_url: params?.brRefUrl ?? "",
          q: String(params?.q ?? ""),
          request_type: "suggest",
        };

        const response = await fetch(
          `${baseUrl}/v2/suggest/?${new URLSearchParams(paramsObj).toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data = await response.json();
        if (!response.ok || !data) {
          return;
        }

        const { queryContext, suggestionGroups = [] } = data;
        const { originalQuery } = queryContext ?? {};
        const { querySuggestions } = suggestionGroups?.[0] ?? {};

        if (queryCached.trim() === originalQuery && querySuggestions) {
          const queryList = querySuggestions?.map((suggestion: { query: string }) =>
            suggestion ? suggestion.query : "",
          );

          setSuggestions(queryList);
        }
      } catch (error) {
        Logger.getLogger().error(`Error during fetching suggestions ${error}`);
        setSuggestions([]);
      }
    },
    [config?.appConfig?.bloomreachSuggestion, queryCached],
  );

  const latestTypedQueryRef = useRef(query);

  const handleQueryChange = useDebouncedCallback((query: string) => {
    setQuery(query);
  }, DEBOUNCE_DELAY_300) as HeaderHelpers["handleQueryChange"];

  const immediateQueryChange = (newQuery: string) => {
    latestTypedQueryRef.current = newQuery;
    handleQueryChange(newQuery);
  };

  const handleQueryClear = useCallback(() => {
    setQuery("");
  }, []);
  const handleSearchClick = useCallback(() => {
    const finalQuery = latestTypedQueryRef.current.trim();

    logBloomreachEvent({
      group: BloomreachGroupType.suggest,
      etype: BloomreachEventType.submit,
      q: finalQuery.trim(),
      catalogs: [
        {
          name: getBloomreachCatalogsAzureKeys(
            pageLocale,
            BloomreachCatalogsAzureKeys.products,
          ),
        },
      ],
      viewId: pageLocale,
    });
    setLastSearches(
      [finalQuery.trim(), ...lastSearches]
        .filter((value, index, self) => self.indexOf(value) === index)
        .slice(0, 10),
    );

    const routerQuery = new URLSearchParams();
    routerQuery.set(Params.QUERY, finalQuery.trim());
    routerQuery.set(Params.TAB, SearchTabs.PRODUCTS);
    const preparedUrl = `/${pageLocale}${Routes.SEARCH}?${routerQuery.toString()}`;
    router.push(preparedUrl);
  }, [pageLocale, setLastSearches, lastSearches, router]);

  const handleSuggestionClick = useCallback(
    (query: string) => {
      const prepared = `${query ?? ""}`.trim();
      logBloomreachEvent({
        group: BloomreachGroupType.suggest,
        etype: BloomreachEventType.click,
        aq: queryCached,
        q: prepared,
        catalogs: [
          {
            name: getBloomreachCatalogsAzureKeys(
              pageLocale,
              BloomreachCatalogsAzureKeys.products,
            ),
          },
        ],
        viewId: pageLocale,
      });
      setLastSearches(
        [prepared, ...lastSearches]
          .filter((value, index, self) => self.indexOf(value) === index)
          .slice(0, 10),
      );
      setQuery(prepared);
      setIsSearchOpen(false);
      const routerQuery = new URLSearchParams();
      routerQuery.set(Params.QUERY, prepared);
      routerQuery.set(Params.TAB, SearchTabs.PRODUCTS);
      const preparedUrl = `/${pageLocale}${Routes.SEARCH}?${routerQuery.toString()}`;
      router.push(preparedUrl);
    },
    [queryCached, pageLocale, setLastSearches, lastSearches, router],
  );
  const handleMenuClose = useCallback(() => setIsMenuOpen(false), []);
  const handleSearchClose = useCallback(() => {
    setIsSearchOpen(false);
  }, []);
  const handleSearchOpen = useCallback(() => {
    setIsSearchOpen(true);
    setIsMenuOpen(false);
  }, []);
  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
    setIsSearchOpen(false);
  }, []);

  const helpers = useMemo(() => {
    return {
      query: query,
      queryCached: queryCached,
      lastSearches: lastSearches,
      suggestions: suggestions,
      isMenuOpen: isMenuOpen,
      isSearchOpen: isSearchOpen,
      handleQueryChange: immediateQueryChange,
      handleQueryClear: handleQueryClear,
      handleSearchClick: handleSearchClick,
      handleMenuClose: handleMenuClose,
      handleMenuOpen: handleMenuOpen,
      handleSearchClose: handleSearchClose,
      handleSearchOpen: handleSearchOpen,
      handleSuggestionClick: handleSuggestionClick,
    } as HeaderHelpers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query,
    queryCached,
    lastSearches,
    suggestions,
    isMenuOpen,
    isSearchOpen,
    handleQueryChange,
    handleQueryClear,
    handleSearchClick,
    handleMenuClose,
    handleMenuOpen,
    handleSearchClose,
    handleSearchOpen,
    handleSuggestionClick,
  ]);

  // this effect will handle the query param changes from the browser. Ex: back, forward, etc.
  useEffect(() => {
    setQuery(queryParam);
  }, [queryParam]);

  useEffect(() => {
    const prepared = queryCached.trim();
    if (prepared.length >= 3) {
      waitForRouterHistory(async () => {
        await loadSuggestions({
          q: prepared,
          brUid2,
          brRefUrl: getRefUrlFromCookie(),
        });
      });
    } else {
      setSuggestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryCached, brUid2]);

  useEffect(() => {
    handleMenuClose();
    handleSearchClose();
  }, [handleMenuClose, handleSearchClose, pathName]);

  return <HeaderContext.Provider value={helpers}>{children}</HeaderContext.Provider>;
};
